import { useState } from 'react';
import type { Market, Selection } from '../services/api';
import { useBetSlip } from '../contexts/BetSlipContext';
import OddsButton from './OddsButton';
import { marketCopy, favouriteTag } from '../utils/betting';

interface MarketCardProps {
  market: Market;
  eventId: string;
  eventName: string;
  /** Event teams — used to render friendly selection labels. */
  homeTeam: string;
  awayTeam: string;
  /** Live-streamed odds override map: selection_id -> odds */
  liveOdds: Record<string, number>;
}

export default function MarketCard({
  market,
  eventId,
  eventName,
  homeTeam,
  awayTeam,
  liveOdds,
}: MarketCardProps) {
  const { legs, addLeg, removeLeg } = useBetSlip();
  const [showHelp, setShowHelp] = useState(false);
  const isSuspended = market.status === 'suspended' || market.status === 'closed';

  const copy = marketCopy(market.market_type, market.name, homeTeam, awayTeam);
  const allOdds = market.selections.map((s) => liveOdds[s.id] ?? s.odds);

  const handleSelection = (selection: Selection, friendlyLabel: string) => {
    if (isSuspended) return;
    const already = legs.some((l) => l.selection_id === selection.id);
    if (already) {
      removeLeg(selection.id);
    } else {
      addLeg({
        selection_id: selection.id,
        market_id: market.id,
        event_id: eventId,
        event_name: eventName,
        market_name: copy.title,
        selection_name: friendlyLabel,
        odds: liveOdds[selection.id] ?? selection.odds,
        stake_cents: 0,
      });
    }
  };

  const cols = Math.min(market.selections.length, 3);

  return (
    <div className="rounded-card border border-border bg-surface p-4 shadow-card">
      {/* Friendly market header */}
      <div className="mb-1 flex items-center justify-between gap-2">
        <h3 className="font-heading text-base font-bold text-text-primary">{copy.title}</h3>
        <div className="flex items-center gap-2">
          {isSuspended && (
            <span className="rounded-pill bg-surface-2 px-2 py-0.5 text-xs italic text-text-muted">
              Paused
            </span>
          )}
          <button
            type="button"
            onClick={() => setShowHelp((v) => !v)}
            aria-label="What does this mean?"
            className="flex h-5 w-5 items-center justify-center rounded-full border border-border text-xs font-bold text-text-muted hover:border-primary hover:text-primary"
          >
            ?
          </button>
        </div>
      </div>

      {/* One-line explainer — collapsed by default, expands on ? tap */}
      <p className={`mb-3 text-xs leading-relaxed text-text-muted ${showHelp ? '' : 'line-clamp-1'}`}>
        {copy.help}
      </p>

      {/* Selections */}
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
        {market.selections.map((sel) => {
          const odds = liveOdds[sel.id] ?? sel.odds;
          const friendly = copy.selectionLabel(sel.name, homeTeam, awayTeam);
          return (
            <OddsButton
              key={sel.id}
              selectionId={sel.id}
              selectionName={friendly}
              odds={odds}
              isSelected={legs.some((l) => l.selection_id === sel.id)}
              isDisabled={isSuspended}
              favTag={favouriteTag(odds, allOdds)}
              onClick={() => handleSelection(sel, friendly)}
            />
          );
        })}
      </div>

      <p className="mt-2 text-[11px] text-text-muted">
        Tap a pick to add it to your bet slip. The R amount shows what a R100 bet returns if it wins.
      </p>
    </div>
  );
}
